import {ReferenceToken} from '@github-ui/copilot-chat/components/ReferenceToken'
import {referenceID} from '@github-ui/copilot-chat/utils/copilot-chat-helpers'
import type {CopilotChatReference} from '@github-ui/copilot-chat/utils/copilot-chat-types'
import {useChatState} from '@github-ui/copilot-chat/utils/CopilotChatContext'
import {useChatManager} from '@github-ui/copilot-chat/utils/CopilotChatManagerContext'
import {testIdProps} from '@github-ui/test-id-props'
import {FocusKeys} from '@primer/behaviors'
import {KebabHorizontalIcon, TrashIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, IconButton, Spinner, useFocusZone} from '@primer/react'
import {clsx} from 'clsx'
import {type RefObject, useMemo, useRef} from 'react'

import {useIsScrolledToEnd} from '../hooks/use-is-scrolled-to-end'
import styles from './ChatInputReferences.module.css'

interface ChatInputReferencesProps {
  className?: string
  /** Called when user attempts to remove one or more references, return false to cancel. */
  onReferenceRemoval?: (references: CopilotChatReference[]) => boolean | undefined
  /** Element to return focus to when all references are deleted. */
  returnFocusRef: RefObject<HTMLElement>
  tokenSize?: 'small' | 'medium'
  isLoading?: boolean
}

const supportedReferenceTypes = new Set<CopilotChatReference['type']>([
  'file',
  'file-v2',
  'folder',
  'symbol',
  'image',
  'issue',
  'text',
])

export const ChatInputReferences = ({
  className,
  isLoading,
  onReferenceRemoval,
  returnFocusRef,
  tokenSize,
}: ChatInputReferencesProps) => {
  const {currentReferences} = useChatState()
  const manager = useChatManager()

  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const scrolledToEnd = useIsScrolledToEnd(scrollContainerRef)

  const selectedFileReferences = useMemo(
    () => currentReferences.filter(reference => supportedReferenceTypes.has(reference.type)),
    [currentReferences],
  )

  const containerRef = useRef<HTMLDivElement>(null)
  useFocusZone(
    {
      containerRef,
      focusableElementFilter: el => !el.hasAttribute('aria-hidden'),
      bindKeys: FocusKeys.ArrowHorizontal | FocusKeys.HomeAndEnd,
      focusInStrategy: 'previous',
      focusOutBehavior: 'stop',
    },
    [selectedFileReferences],
  )

  const tokensRef = useRef<Array<HTMLAnchorElement | null>>([])
  const onRemove = (reference: CopilotChatReference, index: number) => {
    if (onReferenceRemoval?.([reference]) === false) return

    const nextFocusTarget = tokensRef.current[index + 1] ?? tokensRef.current[index - 1] ?? returnFocusRef.current
    nextFocusTarget?.focus()

    manager.removeReference(reference)
  }

  const onRemoveAll = () => {
    if (onReferenceRemoval?.(currentReferences) === false) return

    // setTimeout to delay so we pull focus after the ActionMenu tries to return focus to its (now nonexistent) anchor
    setTimeout(() => returnFocusRef.current?.focus())
    manager.clearCurrentReferences()
  }

  if (selectedFileReferences.length === 0 && !isLoading) return null

  return (
    <div role="toolbar" aria-label="Attachments" className={clsx(className, styles.container)} ref={containerRef}>
      <div
        className={clsx(styles.attachmentsList, scrolledToEnd && styles.scrolledToEnd, isLoading && styles.loading)}
        ref={scrollContainerRef}
      >
        {selectedFileReferences.map((reference, index) => (
          <ReferenceToken
            reference={reference}
            onRemove={() => onRemove(reference, index)}
            size={tokenSize || 'medium'}
            key={referenceID(reference)}
            data-index={index}
            ref={el => {
              tokensRef.current[index] = el
            }}
          />
        ))}

        {isLoading && (
          <div className={styles.functionLoading} {...testIdProps('loading-indicator')}>
            <Spinner size="small" />
            Retrieving…
          </div>
        )}
      </div>

      <div className={styles.divider} />

      <ActionMenu>
        <ActionMenu.Anchor>
          <IconButton
            icon={KebabHorizontalIcon}
            aria-label="Attachments options"
            variant="invisible"
            tooltipDirection="n"
            className={styles.menuButton}
          />
        </ActionMenu.Anchor>

        <ActionMenu.Overlay width="small">
          <ActionList>
            <ActionList.Item onSelect={onRemoveAll}>
              <ActionList.LeadingVisual>
                <TrashIcon />
              </ActionList.LeadingVisual>
              Remove attachments
            </ActionList.Item>
          </ActionList>
        </ActionMenu.Overlay>
      </ActionMenu>
    </div>
  )
}

try{ ChatInputReferences.displayName ||= 'ChatInputReferences' } catch {}